import { useCallback, useEffect, useMemo, useState } from 'react';
import { Globe, Plus, Eye, Edit, Trash2, Shield, CheckCircle, XCircle, Clock } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { WhiteLabelForm } from '../../components/admin/WhiteLabelForm';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { DeleteModal } from '../../components/ui/ActionModals';
import {
  createWhiteLabel,
  deleteWhiteLabel,
  getPricingPlans,
  getWhiteLabels,
} from '../../lib/api';
import {
  WHITE_LABEL_DEFAULT_FORM_VALUES,
  buildWhiteLabelCreatePayload,
  createWhiteLabelFormSchema,
  getWhiteLabelErrorMessage,
  mapWhiteLabelApiErrors,
} from '../../lib/whiteLabelValidation';

const STATUS_STYLES = {
  Active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  Inactive: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  Suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const normalizeStatus = (s) => {
  const v = String(s || '').toLowerCase();
  if (v === 'suspended') return 'Suspended';
  if (v === 'inactive') return 'Inactive';
  return 'Active';
};

const extractPricingPlans = (resp) => {
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.data?.items)) return resp.data.items;
  return [];
};

const mapApiWhiteLabel = (wl) => ({
  id: wl.id,
  brandName: wl.brand_name,
  owner: wl.owner_name,
  email: wl.admin_email || '—',
  phone: wl.phone || '—',
  status: normalizeStatus(wl.status),
  agents: wl?.counts?.agents ?? 0,
  leads: wl?.counts?.leads ?? 0,
  joined: wl.created_at ? new Date(wl.created_at).toISOString().slice(0, 10) : '—',
  plan: (wl.pricing_plan_name || wl.commission_plan || 'standard').toUpperCase(),
  pricingPlanId: wl.pricing_plan_id || '',
  commissionPlan: wl.commission_plan || 'standard',
  customDomain: wl.custom_domain || '',
  primaryColor: wl.primary_color || '#6366f1',
  secondaryColor: wl.secondary_color || '#8b5cf6',
  featurePermissions: wl.feature_permissions || {},
  isApi: true,
});

const buildPaginationItems = (page, totalPages) => {
  const pages = [];
  for (let current = 1; current <= totalPages; current += 1) {
    if (current === 1 || current === totalPages || Math.abs(current - page) <= 1) {
      pages.push(current);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return pages;
};

function TablePagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const items = buildPaginationItems(page, totalPages);
  return (
    <Pagination className="justify-end">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page > 1) onPageChange(page - 1);
            }}
            className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
        {items.map((item, index) => (
          <PaginationItem key={`${item}-${index}`}>
            {item === '...' ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                href="#"
                isActive={item === page}
                onClick={(event) => {
                  event.preventDefault();
                  onPageChange(item);
                }}
              >
                {item}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page < totalPages) onPageChange(page + 1);
            }}
            className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

const useWhiteLabelData = (params = {}) => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
  });

  const refresh = useCallback(async () => {
    if (!token) {
      setItems([]);
      setPagination({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
      setError('Unable to load data.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await getWhiteLabels(token, params);
      const rows = (resp?.data?.items ?? []).map(mapApiWhiteLabel);
      const meta = resp?.data?.pagination || {};
      setItems(rows);
      setPagination({
        page: Number(meta.page || params.page || 1),
        pageSize: Number(meta.page_size || params.pageSize || 10),
        total: Number(meta.total || rows.length || 0),
        totalPages: Number(meta.total_pages || 1),
      });
    } catch (err) {
      setItems([]);
      setPagination({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
      setError('Unable to load data.');
      toast.error(err?.message || 'Failed to load white labels');
    } finally {
      setLoading(false);
    }
  }, [params, token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    token,
    items,
    pagination,
    loading,
    error,
    setItems,
    refresh,
    rows: items,
  };
};

export const AllWhiteLabels = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState({ open: false, whiteLabel: null });

  const navigate = useNavigate();
  const normalizedStatus = statusFilter === 'All' ? undefined : statusFilter.toLowerCase();
  const queryParams = useMemo(() => ({
    q: search.trim() || undefined,
    status: normalizedStatus,
    page,
    pageSize: 10,
  }), [normalizedStatus, page, search]);
  const { token, rows, pagination, loading, error, setItems } = useWhiteLabelData(queryParams);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const summary = useMemo(() => ({
    Active: rows.filter((w) => w.status === 'Active').length,
    Inactive: rows.filter((w) => w.status === 'Inactive').length,
    Suspended: rows.filter((w) => w.status === 'Suspended').length,
  }), [rows]);

  const handleDelete = async () => {
    const whiteLabel = deleteModal.whiteLabel;
    if (!token || !whiteLabel?.isApi) return;
    await deleteWhiteLabel(token, whiteLabel.id);
    setItems((prev) => (prev || []).filter((item) => item.id !== whiteLabel.id));
    setDeleteModal({ open: false, whiteLabel: null });
  };

  return (
    <>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-6 w-6 text-purple-500" />
            <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">All White Labels</h1>
            <span className="ml-2 text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-0.5 rounded-full font-semibold">
              {rows.length} total
            </span>
          </div>
          <Button className="bg-purple-600 hover:bg-purple-700 text-white gap-2" onClick={() => navigate('/admin/whitelabels/add')} size="sm">
            <Plus className="h-4 w-4" />
            Add White Label
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Active', count: summary.Active, color: 'text-emerald-600', icon: CheckCircle },
            { label: 'Inactive', count: summary.Inactive, color: 'text-gray-500', icon: Clock },
            { label: 'Suspended', count: summary.Suspended, color: 'text-red-600', icon: XCircle },
          ].map((s) => (
            <div key={s.label} className="border rounded-xl p-4 flex items-center gap-3">
              <s.icon className={`h-5 w-5 ${s.color}`} />
              <div>
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3 flex-wrap">
          <Input
            placeholder="Search by brand, owner or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
          {['All', 'Active', 'Inactive', 'Suspended'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${statusFilter === s
                ? 'bg-purple-600 text-white border-purple-600'
                : 'border-border hover:bg-accent'
                }`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="rounded-xl border overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">WL ID</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Brand Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Owner Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Email</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Phone</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Plan</th>
                  <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Agents</th>
                  <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Leads</th>
                  <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Joined</th>
                  <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && error && (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">
                      Unable to load data.
                    </td>
                  </tr>
                )}
                {!loading && !error && rows.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">
                      No matching records found.
                    </td>
                  </tr>
                )}
                {!loading && !error && rows.map((wl, idx) => (
                  <tr key={wl.id} className={`border-b transition-colors hover:bg-muted/30 ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}>
                    <td className="px-4 py-3 font-mono text-xs text-purple-600 font-semibold">{wl.id}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 font-bold text-xs">
                          {wl.brandName[0]}
                        </div>
                        <span className="font-medium">{wl.brandName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{wl.owner}</td>
                    <td className="px-4 py-3 text-muted-foreground">{wl.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{wl.phone}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        {wl.plan}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center font-semibold">{wl.agents}</td>
                    <td className="px-4 py-3 text-center font-semibold">{wl.leads}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[wl.status]}`}>
                        {wl.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{wl.joined}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => navigate(`/admin/whitelabels/${wl.id}/view`)}
                          className="p-1.5 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 transition-colors"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/whitelabels/${wl.id}/edit`)}
                          className="p-1.5 rounded-md hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-600 transition-colors"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        
                        {wl.isApi ? (
                          <button
                            onClick={() => setDeleteModal({ open: true, whiteLabel: wl })}
                            className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                {/* {!loading && !error && rows.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-4 py-10 text-center text-muted-foreground">
                      No white labels found.
                    </td>
                  </tr>
                )} */}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between border-t">
          <p className="text-sm text-muted-foreground">
            Showing page {pagination.page} of {pagination.totalPages} · {pagination.total} white labels
          </p>
          <TablePagination page={page} totalPages={pagination.totalPages} onPageChange={setPage} />
        </div>
      </div>
      <DeleteModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, whiteLabel: null })}
        title="White Label"
        description="Are you sure you want to delete this White Label?"
        onConfirm={handleDelete}
      />
    </>
  );
};

export const AddWhiteLabel = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [submitError, setSubmitError] = useState('');
  const form = useForm({
    resolver: zodResolver(createWhiteLabelFormSchema),
    defaultValues: WHITE_LABEL_DEFAULT_FORM_VALUES,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    const loadPlans = async () => {
      if (!token) return;
      try {
        const resp = await getPricingPlans(token);
        setPricingPlans(extractPricingPlans(resp));
      } catch {
        setPricingPlans([]);
      }
    };
    loadPlans();
  }, [token]);

  useEffect(() => {
    const subscription = form.watch(() => {
      if (submitError) {
        setSubmitError('');
      }
    });
    return () => subscription.unsubscribe();
  }, [form, submitError]);

  const handleSubmit = async (values) => {
    if (!token) {
      toast.error('Please login again and retry');
      return;
    }

    setSubmitError('');
    setSubmitting(true);
    try {
      const payload = buildWhiteLabelCreatePayload(values, pricingPlans);
      await createWhiteLabel(token, payload);
      toast.success('White label created successfully');
      navigate('/admin/whitelabels');
    } catch (err) {
      const fieldErrors = mapWhiteLabelApiErrors(err, 'create');
      Object.entries(fieldErrors).forEach(([fieldName, message]) => {
        form.setError(fieldName, { type: 'server', message });
      });
      const firstField = Object.keys(fieldErrors)[0];
      if (firstField) {
        form.setFocus(firstField);
      }

      const message = getWhiteLabelErrorMessage(err, 'Failed to create white label.');
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 max-w-7xl w-full">
      <div className="flex items-center gap-2">
        <Globe className="h-6 w-6 text-purple-500" />
        <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">Add New White Label</h1>
      </div>

      <WhiteLabelForm
        form={form}
        mode="create"
        pricingPlans={pricingPlans}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/admin/whitelabels')}
        submitLabel="Create White Label"
        submitting={submitting}
        submitError={submitError}
      />
    </div>
  );
};

export const WLFeaturePermissions = () => (
  <div className="space-y-5">
    <div className="flex items-center gap-2">
      <Shield className="h-6 w-6 text-purple-500" />
      <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">Feature Permissions</h1>
    </div>
    <div className="rounded-xl border p-6 text-sm text-muted-foreground">
      White label feature permission matrix is now persisted per white label in backend via <code>feature_permissions</code>. UI controls can be expanded here based on API data.
    </div>
  </div>
);

export const WLCommissionPlans = () => (
  <div className="space-y-5">
    <div className="flex items-center gap-2">
      <Globe className="h-6 w-6 text-purple-500" />
      <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">WL Commission Plans</h1>
    </div>
    <div className="rounded-xl border p-6 text-sm text-muted-foreground">
      Commission plans are stored per white label as <code>commission_plan</code> and can be extended to support slab-level overrides.
    </div>
  </div>
);

export const SuspendedWhiteLabels = () => {
  const { token, rows, setItems } = useWhiteLabelData();
  const suspended = rows.filter((w) => w.status !== 'Active');

  const reactivate = async (wl) => {
    if (!token || !wl.isApi) return;
    try {
      await updateWhiteLabelStatus(token, wl.id, 'active');
      setItems((prev) => (prev || []).map((item) => (
        item.id === wl.id ? { ...item, status: 'Active' } : item
      )));
      toast.success('White label reactivated');
    } catch (err) {
      toast.error(err?.message || 'Failed to reactivate');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <XCircle className="h-6 w-6 text-red-500" />
        <h1 className="text-xl font-bold font-['Plus_Jakarta_Sans']">Suspended / Inactive White Labels</h1>
      </div>
      <div className="rounded-xl border overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="text-left px-4 py-3 font-semibold text-muted-foreground">WL ID</th>
              <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Brand Name</th>
              <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Owner</th>
              <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Email</th>
              <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Status</th>
              <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {suspended.map((wl, idx) => (
              <tr key={wl.id} className={`border-b hover:bg-muted/30 ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}>
                <td className="px-4 py-3 font-mono text-xs text-purple-600 font-semibold">{wl.id}</td>
                <td className="px-4 py-3 font-medium">{wl.brandName}</td>
                <td className="px-4 py-3 text-muted-foreground">{wl.owner}</td>
                <td className="px-4 py-3 text-muted-foreground">{wl.email}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[wl.status]}`}>
                    {wl.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => reactivate(wl)}
                    className="px-3 py-1 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                  >
                    Reactivate
                  </button>
                </td>
              </tr>
            ))}
            {suspended.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No suspended white labels.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};
